import React, { useState, useEffect, useRef } from 'react';
import { collection, query, where, getDocs, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../../admin/firebase/config';
import { useOutlet } from '../../context/OutletContext';
import { useWaiterAuth } from '../context/WaiterAuthContext';
import { useTerminal } from '../context/TerminalContext';
import WaiterSidebar from '../components/WaiterSidebar';
import { useReactToPrint } from 'react-to-print';
import './DayReport.css';

const DayReport = () => {
  const { selectedOutlet } = useOutlet();
  const { currentWaiter } = useWaiterAuth();
  const { currentTerminal } = useTerminal();
  const printRef = useRef();

  const [report, setReport] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all'); // all, dine-in, takeaway, delivery

  useEffect(() => {
    fetchDayReport();
  }, [selectedDate, selectedOutlet, filterType]);

  const fetchDayReport = async () => {
    if (!selectedOutlet) return;

    setLoading(true);
    try {
      const startOfDay = new Date(selectedDate);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(selectedDate);
      endOfDay.setHours(23, 59, 59, 999);

      let q;
      if (filterType === 'all') {
        q = query(
          collection(db, 'orders'),
          where('outletId', '==', selectedOutlet.id),
          where('createdAt', '>=', Timestamp.fromDate(startOfDay)),
          where('createdAt', '<=', Timestamp.fromDate(endOfDay)),
          orderBy('createdAt', 'desc')
        );
      } else {
        q = query(
          collection(db, 'orders'),
          where('outletId', '==', selectedOutlet.id),
          where('orderType', '==', filterType),
          where('createdAt', '>=', Timestamp.fromDate(startOfDay)),
          where('createdAt', '<=', Timestamp.fromDate(endOfDay)),
          orderBy('createdAt', 'desc')
        );
      }

      const snapshot = await getDocs(q);
      const orders = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Calculate statistics
      const totalOrders = orders.length;
      const completedOrders = orders.filter(o => o.status === 'completed');
      const totalRevenue = completedOrders.reduce((sum, o) => sum + (o.total || 0), 0);
      const avgOrderValue = completedOrders.length > 0 ? totalRevenue / completedOrders.length : 0;

      const statusCounts = {
        pending: orders.filter(o => o.status === 'pending').length,
        in_progress: orders.filter(o => o.status === 'in_progress').length,
        prepared: orders.filter(o => o.status === 'prepared').length,
        completed: completedOrders.length,
        cancelled: orders.filter(o => o.status === 'cancelled').length
      };

      // Payment method breakdown
      const paymentMethods = {
        cash: 0,
        card: 0,
        upi: 0,
        online: 0
      };

      completedOrders.forEach(order => {
        const method = order.paymentMethod || 'cash';
        if (paymentMethods[method] !== undefined) {
          paymentMethods[method] += order.total || 0;
        }
      });

      // Order type breakdown
      const orderTypes = {
        'dine-in': 0,
        'takeaway': 0,
        'delivery': 0
      };

      orders.forEach(order => {
        const type = order.orderType || 'dine-in';
        if (orderTypes[type] !== undefined) {
          orderTypes[type]++;
        }
      });

      // Top items sold
      const itemCounts = {};
      orders.forEach(order => {
        order.items?.forEach(item => {
          if (itemCounts[item.name]) {
            itemCounts[item.name].count += item.quantity;
            itemCounts[item.name].revenue += item.price * item.quantity;
          } else {
            itemCounts[item.name] = {
              count: item.quantity,
              revenue: item.price * item.quantity
            };
          }
        });
      });

      const topItems = Object.entries(itemCounts)
        .map(([name, data]) => ({ name, ...data }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      // Hourly breakdown
      const hourlyData = {};
      orders.forEach(order => {
        if (order.timestamp?.toDate) {
          const hour = new Date(order.timestamp.toDate()).getHours();
          if (!hourlyData[hour]) {
            hourlyData[hour] = { count: 0, revenue: 0 };
          }
          hourlyData[hour].count++;
          if (order.status === 'completed') {
            hourlyData[hour].revenue += order.total || 0;
          }
        }
      });

      // Waiter performance (if available)
      const waiterPerformance = {};
      orders.forEach(order => {
        if (order.waiterName) {
          if (!waiterPerformance[order.waiterName]) {
            waiterPerformance[order.waiterName] = { orders: 0, revenue: 0 };
          }
          waiterPerformance[order.waiterName].orders++;
          if (order.status === 'completed') {
            waiterPerformance[order.waiterName].revenue += order.total || 0;
          }
        }
      });

      setReport({
        totalOrders,
        totalRevenue,
        avgOrderValue,
        statusCounts,
        paymentMethods,
        orderTypes,
        topItems,
        hourlyData,
        waiterPerformance: Object.entries(waiterPerformance)
          .map(([name, data]) => ({ name, ...data }))
          .sort((a, b) => b.revenue - a.revenue),
        orders
      });

      setLoading(false);
    } catch (error) {
      console.error('Error fetching day report:', error);
      setLoading(false);
    }
  };

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `Day-Report-${selectedDate.toISOString().split('T')[0]}`
  });

  const exportToCSV = () => {
    if (!report || !report.orders) return;

    const headers = ['Order #', 'Table', 'Type', 'Items', 'Total', 'Payment', 'Status', 'Time'];
    const rows = report.orders.map(order => [
      order.orderNumber || '',
      order.tableNumber || '-',
      order.orderType || 'dine-in',
      order.items?.length || 0,
      order.total || 0,
      order.paymentMethod || 'cash',
      order.status || 'pending',
      order.timestamp?.toDate ? new Date(order.timestamp.toDate()).toLocaleString() : ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `day-report-${selectedDate.toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (loading) {
    return (
      <div className="report-loading">
        <div className="loading-spinner"></div>
        <p>Loading report...</p>
      </div>
    );
  }

  if (!report) return null;

  return (
    <div className="day-report-container">
      <WaiterSidebar currentWaiter={currentWaiter} />

      <div className="report-main">
        <div className="report-header">
          <div className="header-left">
            <h1>📊 Day Report</h1>
            <p>{selectedDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
          </div>

          <div className="header-actions">
            <input
              type="date"
              value={selectedDate.toISOString().split('T')[0]}
              onChange={(e) => setSelectedDate(new Date(e.target.value))}
              className="date-picker"
            />

            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Orders</option>
              <option value="dine-in">Dine-In Only</option>
              <option value="takeaway">Takeaway Only</option>
              <option value="delivery">Delivery Only</option>
            </select>

            <button className="btn-export" onClick={exportToCSV}>
              📥 Export CSV
            </button>
            <button className="btn-print" onClick={handlePrint}>
              🖨️ Print Report
            </button>
          </div>
        </div>

        {/* Printable Report */}
        <div ref={printRef} className="report-content">
          {/* Summary Stats */}
          <div className="stats-grid">
            <div className="stat-card revenue-card">
              <div className="stat-icon">💰</div>
              <div className="stat-info">
                <div className="stat-value">₹{report.totalRevenue.toFixed(2)}</div>
                <div className="stat-label">Total Revenue</div>
              </div>
            </div>

            <div className="stat-card orders-card">
              <div className="stat-icon">📋</div>
              <div className="stat-info">
                <div className="stat-value">{report.totalOrders}</div>
                <div className="stat-label">Total Orders</div>
              </div>
            </div>

            <div className="stat-card avg-card">
              <div className="stat-icon">📊</div>
              <div className="stat-info">
                <div className="stat-value">₹{report.avgOrderValue.toFixed(0)}</div>
                <div className="stat-label">Avg Order Value</div>
              </div>
            </div>

            <div className="stat-card completed-card">
              <div className="stat-icon">✅</div>
              <div className="stat-info">
                <div className="stat-value">{report.statusCounts.completed}</div>
                <div className="stat-label">Completed</div>
              </div>
            </div>
          </div>

          {/* Order Status Breakdown */}
          <div className="report-section">
            <h2>📈 Order Status Breakdown</h2>
            <div className="status-grid">
              {Object.entries(report.statusCounts).map(([status, count]) => (
                <div key={status} className="status-item">
                  <div className="status-count">{count}</div>
                  <div className="status-label">{status.replace('_', ' ')}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Payment Methods */}
          <div className="report-section">
            <h2>💳 Payment Methods</h2>
            <div className="payment-grid">
              {Object.entries(report.paymentMethods).map(([method, amount]) => (
                <div key={method} className="payment-item">
                  <div className="payment-method">
                    {method === 'cash' && '💵'}
                    {method === 'card' && '💳'}
                    {method === 'upi' && '📱'}
                    {method === 'online' && '💻'}
                    <span>{method.toUpperCase()}</span>
                  </div>
                  <div className="payment-amount">₹{amount.toFixed(2)}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Top Items */}
          <div className="report-section">
            <h2>🏆 Top Selling Items</h2>
            <table className="report-table">
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Item Name</th>
                  <th>Quantity Sold</th>
                  <th>Revenue</th>
                </tr>
              </thead>
              <tbody>
                {report.topItems.map((item, idx) => (
                  <tr key={idx}>
                    <td className="rank-cell">#{idx + 1}</td>
                    <td className="item-name">{item.name}</td>
                    <td className="quantity-cell">{item.count}</td>
                    <td className="revenue-cell">₹{item.revenue.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Waiter Performance */}
          {report.waiterPerformance.length > 0 && (
            <div className="report-section">
              <h2>👨‍🍳 Waiter Performance</h2>
              <table className="report-table">
                <thead>
                  <tr>
                    <th>Waiter</th>
                    <th>Orders</th>
                    <th>Revenue</th>
                    <th>Avg Order</th>
                  </tr>
                </thead>
                <tbody>
                  {report.waiterPerformance.map((waiter, idx) => (
                    <tr key={idx}>
                      <td className="waiter-name">{waiter.name}</td>
                      <td>{waiter.orders}</td>
                      <td className="revenue-cell">₹{waiter.revenue.toFixed(2)}</td>
                      <td>₹{(waiter.revenue / waiter.orders).toFixed(0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* All Orders */}
          <div className="report-section">
            <h2>📝 All Orders ({report.orders.length})</h2>
            <table className="report-table">
              <thead>
                <tr>
                  <th>Order #</th>
                  <th>Table/Type</th>
                  <th>Items</th>
                  <th>Total</th>
                  <th>Payment</th>
                  <th>Status</th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody>
                {report.orders.map(order => (
                  <tr key={order.id}>
                    <td className="order-number">#{order.orderNumber}</td>
                    <td>{order.tableNumber || order.orderType}</td>
                    <td className="items-count">{order.items?.length || 0}</td>
                    <td className="total-amount">₹{(order.total || 0).toFixed(2)}</td>
                    <td>{order.paymentMethod || 'cash'}</td>
                    <td>
                      <span className={`status-badge status-${order.status}`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="order-time">
                      {order.timestamp?.toDate ? new Date(order.timestamp.toDate()).toLocaleTimeString() : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DayReport;
